/**
 * src/components/Financial/modules/AspectReturnsModule.jsx
 * 【完全修正版：省略なし・サイデリアル対応・ダークモード完全準拠】
 */
import React, { useMemo } from "react"; // useMemo を追加
import { Zap, Info } from "lucide-react";
import * as Astro from "../../../astronomy.js";
import { PLANET_SYMBOLS, PLANET_COLORS } from "../../../utils/constants.js";

/**
 * ピボット（主要高値・安値）を自動判定する内部関数
 */
const findPivots = function (timeline, period) {
  var pivots = new Set();
  if (!timeline || timeline.length === 0) return pivots;
  var p = period || 5;

  for (var i = p; i < timeline.length - p; i++) {
    var current = timeline[i];
    var isHigh = true;
    var isLow = true;

    for (var j = i - p; j <= i + p; j++) {
      if (i === j) continue;
      var other = timeline[j];

      // 高値判定 (近傍 p バーの中で最高)
      var currH = current.High !== undefined ? current.High : current.price;
      var otherH = other.High !== undefined ? other.High : other.price;
      if (otherH >= currH) isHigh = false;

      // 安値判定 (近傍 p バーの中で最低)
      var currL = current.Low !== undefined ? current.Low : current.price;
      var otherL = other.Low !== undefined ? other.Low : other.price;
      if (otherL <= currL) isLow = false;
    }

    if (isHigh || isLow) {
      pivots.add(i);
    }
  }
  return pivots;
};

/**
 * アスペクト記号の定義
 */
var ASPECT_SYMBOLS = {
  0: "☌",
  45: "∠",
  90: "□",
  135: "⚼",
  180: "☍",
  225: "□",
  270: "□",
  315: "∠",
};

/**
 * 惑星位置の取得（サイデリアル補正対応）
 */
const getLon = function (planet, date, isHelio, isSidereal) {
  var lon = Astro.getAnyPlanetLongitude(planet, date, isHelio);
  if (lon === null || lon === undefined) return null;
  if (isSidereal) {
    var ayan =
      typeof Astro.calculateLahiriAyanamsha === "function"
        ? Astro.calculateLahiriAyanamsha(date)
        : 23.8528;
    lon = (lon - ayan + 360) % 360;
  }
  return lon;
};

/**
 * アスペクトリターンズ 計算ロジック
 */
export const calculateAspectReturns = function (
  timeline,
  baseDate,
  selectedPlanets,
  selectedAngles,
  isHelio,
  isSidereal,
) {
  var angles =
    selectedAngles && selectedAngles.length > 0
      ? selectedAngles
      : [0, 90, 180, 270];
  var planetArray = Array.from(selectedPlanets || []);

  if (
    !baseDate ||
    !timeline ||
    timeline.length === 0 ||
    planetArray.length < 2
  ) {
    return [];
  }

  var baseAspects = [];
  var baseDateObj = new Date(baseDate);

  // 1. 基準日において形成されているアスペクト（オーブ2.0度以内）を抽出
  for (var i = 0; i < planetArray.length; i++) {
    for (var j = i + 1; j < planetArray.length; j++) {
      var p1 = planetArray[i];
      var p2 = planetArray[j];
      var lon1 = getLon(p1, baseDateObj, isHelio, isSidereal);
      var lon2 = getLon(p2, baseDateObj, isHelio, isSidereal);

      if (lon1 === null || lon2 === null) continue;

      var diff = Math.abs(lon1 - lon2);
      if (diff > 180) diff = 360 - diff;

      for (var k = 0; k < angles.length; k++) {
        if (Math.abs(diff - angles[k]) <= 2.0) {
          baseAspects.push({ p1: p1, p2: p2, angle: angles[k] });
          break;
        }
      }
    }
  }

  if (baseAspects.length === 0) return [];

  // 2. タイムラインをスキャンして、アスペクトが「正確な角度」を通過する瞬間（ヒット）を特定
  var hits = [];
  for (var t = 1; t < timeline.length; t++) {
    var d1 = timeline[t - 1].timestamp
      ? new Date(timeline[t - 1].timestamp)
      : new Date(timeline[t - 1].Date);
    var d2 = timeline[t].timestamp
      ? new Date(timeline[t].timestamp)
      : new Date(timeline[t].Date);

    baseAspects.forEach(function (ba) {
      var getV = function (date) {
        var l1 = getLon(ba.p1, date, isHelio, isSidereal);
        var l2 = getLon(ba.p2, date, isHelio, isSidereal);
        if (l1 === null || l2 === null) return null;
        var d = (l1 - l2 + 360) % 360;
        var res = d - ba.angle;
        if (res > 180) res -= 360;
        if (res < -180) res += 360;
        return res;
      };
      var v1 = getV(d1);
      var v2 = getV(d2);
      if (
        v1 !== null &&
        v2 !== null &&
        ((v1 <= 0 && v2 > 0) || (v1 >= 0 && v2 < 0))
      ) {
        hits.push({
          index: t,
          p1: ba.p1,
          p2: ba.p2,
          angle: ba.angle,
          symbol: ASPECT_SYMBOLS[ba.angle] || "★",
          color: "#f39c12",
        });
      }
    });
  }
  return hits;
};

/**
 * アスペクトリターンズ 描画レイヤー (ピボット重複マーカー機能付き)
 */

/**
 * アスペクトリターンズ 描画レイヤー (クラッシュ防止ガード・ピボット重複マーカー機能付き)
 */
export const AspectReturnLayer = function (props) {
  var hits = props.hits;
  var timeline = props.timeline || [];
  var displayRange = props.displayRange;
  var coords = props.coords;
  var planetColors = props.planetColors || {};
  var isDarkMode = props.isDarkMode;

  // 自動ピボット判定 (5バー期間)
  var pivots = useMemo(
    function () {
      return typeof findPivots === "function"
        ? findPivots(timeline, 5)
        : new Set();
    },
    [timeline],
  );

  if (!hits || hits.length === 0 || !coords) return null;

  return (
    <g className="aspect-return-layer">
      {hits.map(function (hit, i) {
        if (
          hit.index < displayRange.start ||
          hit.index >= displayRange.start + displayRange.count
        ) {
          return null;
        }

        var xPos = coords.getX(hit.index - displayRange.start);
        var color1 = planetColors[hit.p1] || PLANET_COLORS[hit.p1] || "#64748b";
        var color2 = planetColors[hit.p2] || PLANET_COLORS[hit.p2] || "#64748b";

        // --- マーカー表示用の安全な計算処理 ---
        var isPivotOverlap = pivots.has(hit.index);
        var markerY = 0;
        var hasGetY = typeof coords.getY === "function";

        if (isPivotOverlap && hasGetY) {
          var node = timeline[hit.index];
          var highVal = node
            ? node.High !== undefined
              ? node.High
              : node.price || 0
            : 0;
          markerY = coords.getY(highVal) - 15;
        }

        var textStyle = {
          paintOrder: "stroke",
          stroke: isDarkMode ? "#0b0e14" : "#ffffff",
          strokeWidth: "3px",
          strokeLinejoin: "round",
        };

        return (
          <g key={"as-ret-hit-" + i}>
            {/* 垂直グリッド線 */}
            <line
              x1={xPos}
              y1={coords.pad.t}
              x2={xPos}
              y2={coords.pad.t + coords.iH}
              stroke={hit.color}
              strokeDasharray="2,2"
              opacity="0.5"
            />

            {/* ピボット重複マーカー (●) */}
            {isPivotOverlap && hasGetY && (
              <circle
                cx={xPos}
                cy={markerY}
                r="4"
                fill={hit.color}
                style={{
                  paintOrder: "stroke",
                  stroke: isDarkMode ? "#0b0e14" : "#ffffff",
                  strokeWidth: "2px",
                }}
              />
            )}

            <g
              transform={"translate(" + xPos + ", " + (coords.pad.t - 45) + ")"}
            >
              <text
                textAnchor="middle"
                fontSize="16"
                fontWeight="bold"
                style={textStyle}
              >
                <tspan fill={color1}>{PLANET_SYMBOLS[hit.p1]}</tspan>
                <tspan
                  fill={isDarkMode ? "rgba(255,255,255,0.3)" : "#64748b"}
                  fontSize="12"
                >
                  {" "}
                </tspan>
                <tspan fill={color2}>{PLANET_SYMBOLS[hit.p2]}</tspan>
              </text>
              {/* 修正箇所：Ba参照を削除し、hit.colorを使用 */}
              <text
                y="18"
                textAnchor="middle"
                fontSize="18"
                fill={hit.color}
                fontWeight="bold"
                style={textStyle}
              >
                {hit.symbol}
              </text>
              <text
                y="32"
                textAnchor="middle"
                fontSize="10"
                fill={hit.color}
                fontWeight="bold"
                style={textStyle}
              >
                {hit.angle}°
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
};

/**
 * 設定UI (他モジュールとデザイン統一版)
 */
export const AspectReturnsSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow;
  var selectedPlanets = props.selectedPlanets;
  var setSelectedPlanets = props.setSelectedPlanets;
  var selectedAngles = props.selectedAngles || [0, 90, 180, 270];
  var setSelectedAngles = props.setSelectedAngles;
  var isDarkMode = props.isDarkMode;

  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
  };

  var planets = [
    "Sun",
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];
  var anglesList = [0, 45, 90, 135, 180, 225, 270, 315];

  var togglePlanet = function (p) {
    var next = new Set(selectedPlanets);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setSelectedPlanets(next);
  };

  var toggleAngle = function (a) {
    var next =
      selectedAngles.indexOf(a) !== -1
        ? selectedAngles.filter(function (x) {
            return x !== a;
          })
        : selectedAngles.concat([a]);
    setSelectedAngles(next);
  };

  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  var plBtnSt = function (p, active) {
    var pColor = PLANET_COLORS[p] || "#6366f1";
    return {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 14px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: "600",
      cursor: "pointer",
      background: active
        ? isDarkMode
          ? "#374151"
          : "#fff"
        : isDarkMode
          ? "transparent"
          : "#f1f5f9",
      border:
        "1.5px solid " +
        (active ? pColor : isDarkMode ? "rgba(255,255,255,0.05)" : "#e2e8f0"),
      color: active
        ? isDarkMode
          ? "#fff"
          : pColor
        : isDarkMode
          ? "rgba(255, 255, 255, 0.4)"
          : "#64748b",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: theme.text,
          }}
        >
          <Zap size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>アスペクトリターンズ (Aspect Returns)</span>
        </div>
        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "15px",
          color: theme.subText,
        }}
      >
        基準日設定 : 対象日のローソク足をダブルクリック。
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              color: theme.subText,
              marginBottom: "12px",
            }}
          >
            対象惑星 (2つ以上):
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {planets.map(function (p) {
              return (
                <button
                  key={p}
                  onClick={function () {
                    togglePlanet(p);
                  }}
                  style={plBtnSt(p, selectedPlanets.has(p))}
                >
                  {PLANET_SYMBOLS[p]} {p}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div
            style={{
              fontSize: "11px",
              fontWeight: "bold",
              color: theme.subText,
              marginBottom: "10px",
            }}
          >
            角度:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {anglesList.map(function (a) {
              var isSelected = selectedAngles.indexOf(a) !== -1;
              return (
                <button
                  key={a}
                  onClick={function () {
                    toggleAngle(a);
                  }}
                  style={btnSt(isSelected)}
                >
                  {a}°
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 基準点（Base Date）の形成アスペクト一覧表示 (ダークモード対応)
 */
export const BaseDateAspectList = function (props) {
  var baseDate = props.baseDate;
  var selectedPlanets = props.selectedPlanets;
  var selectedAngles = props.selectedAngles || [0, 90, 180, 270];
  var isHelio = props.isHelio;
  var isSidereal = props.isSidereal;
  var planetColors = props.planetColors || {};
  var isDarkMode = props.isDarkMode;

  if (!baseDate || !selectedPlanets) return null;

  var planetArray = Array.from(selectedPlanets);
  var baseDateObj = new Date(baseDate);
  var found = [];

  for (var i = 0; i < planetArray.length; i++) {
    for (var j = i + 1; j < planetArray.length; j++) {
      var p1 = planetArray[i];
      var p2 = planetArray[j];
      var lon1 = getLon(p1, baseDateObj, isHelio, isSidereal);
      var lon2 = getLon(p2, baseDateObj, isHelio, isSidereal);
      if (lon1 === null || lon2 === null) continue;

      var diff = Math.abs(lon1 - lon2);
      if (diff > 180) diff = 360 - diff;

      for (var k = 0; k < selectedAngles.length; k++) {
        var target = selectedAngles[k];
        var orb = Math.abs(diff - target);
        if (orb <= 2.0) {
          found.push({ p1: p1, p2: p2, angle: target, orb: orb });
          break;
        }
      }
    }
  }

  if (found.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "85px",
        left: "180px",
        backgroundColor: isDarkMode
          ? "rgba(28, 31, 38, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        color: isDarkMode ? "#ffffff" : "#1e293b",
        padding: "12px",
        borderRadius: "10px",
        fontSize: "11px",
        zIndex: 105,
        border: "1px solid " + (isDarkMode ? "#2d313d" : "#e2e8f0"),
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
        minWidth: "160px",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          fontSize: "10px",
          color: "#f39c12",
          marginBottom: "4px",
          borderBottom: "1.5px solid #f39c12",
          paddingBottom: "2px",
        }}
      >
        基準点アスペクト (Orb 2.0)
      </div>
      {found.map(function (as, idx) {
        var c1 =
          planetColors[as.p1] ||
          PLANET_COLORS[as.p1] ||
          (isDarkMode ? "#fff" : "#333");
        var c2 =
          planetColors[as.p2] ||
          PLANET_COLORS[as.p2] ||
          (isDarkMode ? "#fff" : "#333");
        return (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span style={{ color: c1, fontSize: "14px" }}>
                {PLANET_SYMBOLS[as.p1]}
              </span>
              <span
                style={{
                  color: isDarkMode ? "rgba(255,255,255,0.3)" : "#94a3b8",
                }}
              >
                -
              </span>
              <span style={{ color: c2, fontSize: "14px" }}>
                {PLANET_SYMBOLS[as.p2]}
              </span>
              <span
                style={{
                  marginLeft: "4px",
                  fontWeight: "bold",
                  color: "#f39c12",
                }}
              >
                {ASPECT_SYMBOLS[as.angle]}
              </span>
            </div>
            <span
              style={{
                color: isDarkMode ? "#ccc" : "#1e293b",
                fontFamily: "monospace",
              }}
            >
              {as.angle}° ({as.orb.toFixed(2)}°)
            </span>
          </div>
        );
      })}
    </div>
  );
};
